/*
 * Developed for EPSON AMERICA INC. by Softeq Development Corporation
 * http://www.softeq.com
 */

import { CARD_TYPES } from 'constants/payment'

// hasValidFunctions
// tslint:disable
export const hasValidEmail = value => /^(([^<>()\[\]\\.,;:\s@".?!'#$%^*&]+(\.[^<>()\[\]\\.,;:\s@".?!'#$%^*&)]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(value)
export const hasValidPassword = (value = '') => {
  return value.length >= 8 &&
  /^(?=.*[a-zа-яà-ž])(?=.*[A-ZА-ЯÀ-Ž])(?=.*[0-9])(?=.*[-*.!@#$%^&(){}[\]:";'<>,?/~`_+=|\\]).{8,}$/g.test(value)
}
export const hasPasswordUppercaseLetters = (value = '') => /^(?=.*[A-ZА-ЯÀ-Ž])/g.test(value)
export const hasPasswordLowercaseLetters = (value = '') => /^(?=.*[a-zа-яà-ž])/g.test(value)
export const hasPasswordNumbers = (value = '') => /^(?=.*[0-9])/g.test(value)
export const hasPasswordSpecialCharacters = (value = '') => /^(?=.*[-*.!@#$%^&(){}[\]:";'<>,?/~`_+=|\\])/g.test(value)
export const hasPasswordValidLength = (value = '') => value.length >= 8 && value.length <= 20

// tslint:enable
export const hasRequired = (value = '') => {
  if (!value) {
    return false
  }
  return value.trim().length > 0
}
export const hasRequiredCheckbox = value => {
  return value
}
export const hasOnlyLetters = value => /^[A-zÀ-ž]+$/g.test(value)
export const hasOnlyNumbers = value => /^[0-9]+$/g.test(value)
export const hasOnlyAlphanumeric = value => /^[0-9A-zÀ-ž]+$/g.test(value)
export const hasPhoneNumber = value => /^[0-9\-]{12}$/g.test(value)
export const hasCardNumber = (value = '', cardType) => {
  if (cardType === CARD_TYPES.amex) {
    return value.replace(/-|_/g, '').length === 15
  }

  return value.replace(/-|_/g, '').length === 16
}
export const hasCardCVV = (value, cardType) => {
  if (cardType !== CARD_TYPES.amex) {
    return /^[0-9]{3}$/g.test(value)
  }

  return /^[0-9]{4}$/g.test(value)
}
export const hasNameOnCard = value => {
  if (/\s/.test(value)) {
    const parts = value.split(' ')
    const name = parts.filter(word => word)
    return name.length > 1 && name.every(word => hasName(word))
  }

  return false
}
export const hasFullName = value => {
  if (/\s/.test(value)) {
    const parts = value.split(' ')
    const name = parts.filter(word => word)
    return name.length > 1
  }
  return false
}
export const hasValidSecretAnswer = (value = '') => /^[0-9A-zÀ-ž\s]+$/g.test(value)
export const hasMoreMinLength = min => (value = '') => value.trim().length >= min
export const hasLessMaxLength = max => (value = '') => value.trim().length <= max
export const hasRequiredLength = requiredLength => (value = '') => value.trim().length === requiredLength
export const hasFloatNumber = value => /^[0-9]+\.*[0-9]*$/g.test(value)
export const hasValidDateRange = value => {
  if (value) {
    const startDate = new Date(value.startDate)
    const endDate = new Date(value.endDate)
    return endDate.valueOf() - startDate.valueOf() >= 0
  }
  return true
}
export const hasName = value => /^[A-Za-zÀ-ž\-\'\‘\-]+$/.test(value)
export const hasSelectedValue = value => value !== undefined && value !== null

export const hasValidMultipleEmails = ({ max, min = 0 }) => (value = '') => {
  const valueWithoutSpaces = value.replace(/\s+/g, '')

  if (min === 0 && valueWithoutSpaces.length === 0) {
    return true
  }

  const emails = valueWithoutSpaces.split(',')

  if (emails.length < min || emails.length > max) {
    return false
  }

  for (const email of emails) {
    if (email.length < 5 || email.length > 60) {
      return false
    }
    if (!hasValidEmail(email)) {
      return false
    }
  }

  return true
}

export const hasMoreMinValue = minValue => value => minValue <= value

export const hasLink = value =>
  /(?:http(s)?:\/\/)[\w.-]+(?:\.[\w\.-]+)+[\wА-я\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/g.test(value)

export const hasValidPostalCode = (value = '') => {
  if (value.length > 5) {
    return /^[0-9]{5}-[0-9]{4}$/.test(value)
  }
  return /^[0-9]{5}$/.test(value)
}

export const hasAddressWithoutPOBox = (value = '') => {
  return !/\bP(ost|ostal)?([ \.]*(O|0)(ffice)?)?([ \.]*B(ox)?)?\b/i.test(value)
}

// utils
export const createValidation = validators => value => {
  const messages = []
  validators.forEach(validator => {
    if (!validator(value)) {
      messages.push(typeof validator.message === 'function' ? validator.message() : validator.message)
    }
  })
  if (messages.length) {
    return messages
  }
}

export const createValidator = (hasValid, message) => {
  const validator: any = value => hasValid(value)
  validator.message = message
  return validator
}

type IValidation = (value: any) => string[] | undefined

interface IValidationsByName {
  [name: string]: IValidation,
}

export const createSyncFormValidation = (validationsByName: IValidationsByName) => values => {
  const messages = {}
  Object.entries(validationsByName).forEach(([name, validation]) => {
    messages[name] = validation(values[name])
  })
  return messages
}

export const onBeforeChangePostalCode = ({ value, selection }) => {
  if (value.length > 5 && value[value.length - 1] !== '-') {
    return {
      value,
      selection: {
        start: value.length,
        end: value.length,
      },
    }
  }
  if (value[value.length - 1] === '-') {
    const newValue = value.substr(0, value.length - 1)
    return {
      value: newValue,
      selection: {
        start: newValue.length,
        end: newValue.length,
      },
    }
  }
  return {
    value,
    selection,
  }
}

// normalizations
export const removeSpaces = (value = '') => value.replace(/\s+/g, '')
export const onlyDigits = (value = '') => value.replace(/[^0-9]+/, '')
export const removeDashes = (value = '') => value.replace(/-+/g, '')
